import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { registrationFormCompleteSchema } from 'utils/form-validation-schema';
import { saveRegistrationInformation, getStates } from 'services/UserAccountService';
import { shapeRegistrationPayload } from './registration-long-form-utils';
import { RegistrationFormHeader } from './RegistrationFormHeader';
import { RegistrationFormFooter } from './RegistrationFormFooter';
import { LoginInformation } from './RegistrationFormSections/LoginInformation';
import { ContactInformation } from './RegistrationFormSections/ContactInformation';
import { AddressInformation } from './RegistrationFormSections/AddressInformation';

export const RegistrationFormComplete = (props) => {
  const { Account, Prefixes, DateOfBirth, States, Countries } = props;
  const [selectedPrefix, setSelectedPrefix] = useState('');
  const [selectedDobDay, setSelectedDobDay] = useState('');
  const [selectedDobMonth, setSelectedDobMonth] = useState('');
  const [selectedDobYear, setSelectedDobYear] = useState('');
  const [selectedState, setSelectedState] = useState('VIC');
  const [selectedCountry, setSelectedCountry] = useState('Australia');
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [bottomErrorMessage, setBottomErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [complexErrorMessage, setComplexErrorMessage] = useState('');
  const [subscribeToNewsletters, setSubscribeToNewsletters] = useState(true);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [statesObject, setStatesObject] = useState(States);
  const [selectedStateDescription, setSelectedStateDescription] = useState('Victoria');

  const methods = useForm({
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: yupResolver(registrationFormCompleteSchema),
    defaultValues: {
      regInputEmail: Account?.Email,
      regFirstName: Account?.FirstName,
      regLastName: Account?.LastName,
    },
  });

  const {
    formState: { isValid, errors },
  } = methods;

  const saveUserInformation = async (requestPayload) => {
    setIsProcessing(true);
    const response = await saveRegistrationInformation(requestPayload);

    if (response?.SuccessMessage) {
      setSuccessMessage(response.SuccessMessage);
      setErrorMessage('');
      setBottomErrorMessage('');
      setComplexErrorMessage('');
    } else {
      setErrorMessage(response.ErrorMessage);
      setComplexErrorMessage(response.ComplexErrorMessage);
      setBottomErrorMessage(
        'Whoops! The form has errors that need to be fixed before you can continue.'
      );

      setSubmitButtonDisabled(true);
    }

    setIsProcessing(false);
  };

  const handleRegistrationFormSubmit = (inputsData) => {
    const requestPayload = shapeRegistrationPayload({
      inputsData,
      dropdownsData: {
        selectedDobDay,
        selectedDobMonth,
        selectedDobYear,
        selectedPrefix,
        selectedState,
        selectedCountry,
      },
      subscribeToNewsletters,
    });

    const stateObj = statesObject.find((state) => state.StateCode === selectedState);
    const stateId = stateObj?.Id || 0;
    const stateDescription = stateObj.Description;
    requestPayload.AddressModel.StateId = stateId;
    requestPayload.AddressModel.StateDescription = stateDescription;

    const countryId = Countries.find((country) => country.Description === selectedCountry).Id;
    requestPayload.AddressModel.CountryId = countryId;

    saveUserInformation(requestPayload);
  };

  const selectedCountryChangeHandler = async (selectedCountryDesc) => {
    const countryId = Countries.find(
      (countryObj) => countryObj.Description === selectedCountryDesc
    ).Id;

    const response = await getStates(countryId);

    if (response.length) {
      const newStatesArray = response.map((state) => {
        const newStateObj = {
          Country: state.country,
          Description: state.description,
          Inactive: state.inactive,
          Id: state.id,
          StateCode: state.stateCode,
        };

        return newStateObj;
      });

      const defaultStateIndex = 7;
      setStatesObject(newStatesArray);
      setSelectedState(newStatesArray[defaultStateIndex]?.StateCode);
      setSelectedStateDescription(newStatesArray[defaultStateIndex]?.Description);
    } else {
      setStatesObject([{ StateId: 0, StateCode: '', Description: 'N/A' }]);
      setSelectedState('');
      setSelectedStateDescription('N/A');
    }
  };

  useEffect(() => {
    setSubmitButtonDisabled(!isValid);
  }, [isValid]);

  return (
    <section className="container cart-content registration-form">
      <RegistrationFormHeader
        noValidate="novalidate"
        errorMessage={errorMessage}
        complexErrorMessage={complexErrorMessage}
      />

      <FormProvider {...methods}>
        <form
          className="form-register"
          name="registerForm"
          noValidate="novalidate"
          onSubmit={methods.handleSubmit(handleRegistrationFormSubmit)}
        >
          <div className="row">
            <div className="col-sm-6 col-md-5 col-md-offset-1 col-lg-4">
              <LoginInformation errors={errors} />

              <ContactInformation
                Prefixes={Prefixes}
                DateOfBirth={DateOfBirth}
                setSelectedPrefix={setSelectedPrefix}
                setSelectedDobDay={setSelectedDobDay}
                setSelectedDobMonth={setSelectedDobMonth}
                setSelectedDobYear={setSelectedDobYear}
              />

              <AddressInformation
                States={statesObject}
                Countries={Countries}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                setSelectedCountry={setSelectedCountry}
                selectedCountryChangeHandler={selectedCountryChangeHandler}
                selectedStateDescription={selectedStateDescription}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-8 col-md-offset-1">
              <RegistrationFormFooter
                isProcessing={isProcessing}
                submitButtonDisabled={!isValid || submitButtonDisabled}
                bottomErrorMessage={bottomErrorMessage}
                complexErrorMessage={complexErrorMessage}
                successMessage={successMessage}
                subscribeToNewsletters={subscribeToNewsletters}
                setSubscribeToNewsletters={setSubscribeToNewsletters}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </section>
  );
};

RegistrationFormComplete.propTypes = {
  Account: PropTypes.object,
  Prefixes: PropTypes.arrayOf(PropTypes.object),
  AddressModel: PropTypes.object,
  DateOfBirth: PropTypes.object,
  States: PropTypes.arrayOf(PropTypes.object),
  StateDescription: PropTypes.string,
  Countries: PropTypes.arrayOf(PropTypes.object),
};

export const RegistrationFormCompleteSC = (props) => {
  const { sitecoreData } = props;
  return <RegistrationFormComplete {...sitecoreData} />;
};

RegistrationFormCompleteSC.propTypes = {
  sitecoreData: PropTypes.object,
};
